import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form'; // Added Form import for potential future use or consistency
import s from './SequenceFileDropzone.module.scss';

const SequenceFileDropzone = ({ uploadedFiles = [], flowSequences = [], onValidateCallback, updateDetailsFromSequenceConfig,
  fileUploadStatus, onFileUploadStatusChangeCallback, intl }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [viewFileId, setViewFileId] = useState(0);
  const [filesWithErrors, setFilesWithErrors] = useState(new Set());
  const [FilesToBeUploaded, setFilesToBeUploaded] = useState([]);

  // Prevent default drag and drop behavior globally
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Prevent browser from opening files
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);

    return () => {
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    // Reset input to allow selecting the same files again
    e.target.value = '';
  };

  const validateJSONFile = (file) => {
    const isJSON = file.type === 'application/json' || file.name.endsWith('.json');
    return isJSON;
  };

  const handleFiles = async (fileList) => {
    setError('');

    const validFiles = fileList.filter(file => {
      if (!validateJSONFile(file)) {
        setError(prev => prev + `${file.name} is not a JSON file. `);
        return false;
      }
      return true;
    });

    // Read all files and wait for all promises to complete
    const filePromises = validFiles.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const content = e.target.result;
            const parsedJSON = JSON.parse(content);

            resolve({
              id: Date.now() + Math.random(),
              name: file.name,
              size: file.size,
              content: parsedJSON,
              rawContent: content
            });
          } catch (parseError) {
            setError(prev => prev + `Error parsing ${file.name}: ${parseError.message}. `);
            reject(parseError);
          }
        };

        reader.onerror = () => {
          const errorMsg = `Error reading ${file.name}. `;
          setError(prev => prev + errorMsg);
          reject(new Error(errorMsg));
        };

        reader.readAsText(file);
      });
    });

    try {
      // Wait for all files to be read
      const newFiles = await Promise.allSettled(filePromises);

      // Filter out rejected promises and extract fulfilled values
      const successfulFiles = newFiles
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      // Update state with new files, replacing duplicates by filename
      if (successfulFiles.length > 0) {
        setFiles(prevFiles => {
          // Create a map of existing files by filename for quick lookup
          const fileMap = new Map(prevFiles.map(file => [file.name, file]));

          // Overwrite or add new files
          successfulFiles.forEach(newFile => {
            fileMap.set(newFile.name, newFile);
          });

          // Convert map back to array
          const updatedFiles = Array.from(fileMap.values());


          return updatedFiles;
        });

        // Call the onUploadComplete callback with the new files
        //To update the sequence version details from sequence_config.json
        if (updateDetailsFromSequenceConfig && isSequenceConfigFilePresent(successfulFiles)) {
          const sequenceConfigFile = successfulFiles.find(file => file.name === 'sequence_config.json');
          if (!sequenceConfigFile || !sequenceConfigFile.content) {
            return;
          }
          let sequenceConfigFileContent = sequenceConfigFile.content;
          if (!sequenceConfigFileContent.sequence) {
            return
          }
          let stateMachineData = sequenceConfigFileContent.sequence.state_machine || {};
          let startStateId = 0;
          if (stateMachineData.start_state) {
            let start_state_key = stateMachineData.start_state;
            let start_state = stateMachineData?.states?.transition_states[start_state_key];
            if (start_state) {
              let startStateSequenceCode = start_state.state_id || 0;
              if (startStateSequenceCode) {
                startStateId = flowSequences.find(fs => fs.SequenceCode === startStateSequenceCode)?.SetpointId || 0;
              }
            }
          }
          let sequenceVersionData = {
            VersionNo: sequenceConfigFileContent.sequence.version || '',
            Name: sequenceConfigFileContent.sequence.name || '',
            Description: sequenceConfigFileContent.sequence.description || '',
            VerifyErrorCode: stateMachineData.verify_error_code && stateMachineData.verify_error_code.toString() || null,
            VerifyWarningCode: stateMachineData.verify_warning_code && stateMachineData.verify_warning_code.toString() || null,
            StartStateId: startStateId
          }
          updateDetailsFromSequenceConfig(sequenceVersionData);
        }

      }

    } catch (err) {
      console.error('Error processing files:', err);
    }
  };

  const onSelectCompleteInternal = () => {
    //VerifiedData will be null if the files are not valid
    let verifiedData = verifiySequenceInFlowSequences(files);
    return verifiedData;
  }

  //Wiring up the onValidateCallback
  if (onValidateCallback) {
    onValidateCallback(onSelectCompleteInternal)
  }

  const onFileUploadStatusChangeInternal = (fileId, statusMessage) => {
    if (files && fileId && statusMessage) {
      // ✅ Create new array with updated object
      const statusUpdateFiles = files.map(f =>
        f.id == fileId
          ? { ...f, statusMessage: statusMessage } // Create new object with updated status
          : f // Keep other files unchanged
      );

      // Set the state with the new array
      setFiles(statusUpdateFiles); // This will trigger re-render
    }
  }

  //Wiring up the onFileUploadStatusChangeCallback
  if (onFileUploadStatusChangeCallback) {
    onFileUploadStatusChangeCallback(onFileUploadStatusChangeInternal)
  }


  const removeFile = (id) => {
    setFiles(prevFiles => prevFiles.filter(file => file.id !== id));
    // Clear error state for removed file
    setFilesWithErrors(prev => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
  };

  const viewFile = (id) => {
    setViewFileId(id);
  };


  const clearAll = () => {
    setFiles([]);
    setError('');
    setFilesWithErrors(new Set());
  };

  const dragDropFileMessage = intl.formatMessage({ id: 'sequenceVersionManagement.dragAndDropJsonFile', defaultMessage: 'Drag and drop JSON files here or click to browse' });
  const jsonFileSupportMessage = intl.formatMessage({ id: 'sequenceVersionManagement.onlyJsonFilesSupported', defaultMessage: 'Only .json files are supported' });
  const dropFileMessage = intl.formatMessage({ id: 'sequenceVersionManagement.dropJSONFile', defaultMessage: 'Drop JSON files here...' });
  const filesWithoutSetpointError = intl.formatMessage({ id: 'sequenceVersionManagement.filesWithoutSetpointError', defaultMessage: 'Flow sequence setpoints are missing for the highlighted files' });

  //File processing logic Starts Here

  //check if there is sequence_config.json file exists in the uploaded files
  const isSequenceConfigFilePresent = (selectedFiles) => {
    return selectedFiles.some(file => file.name === 'sequence_config.json')
  };


  //Merging logic to combine sequence files with sequence_config.json, flowSequences and already uploaded files
  const mergeSetpointsSequenceConfigAndUploadedFileToSelectedFiles = (selectedFiles) => {
    let sequenceConfigFile = selectedFiles.find(file => file.name === 'sequence_config.json');

    if (!sequenceConfigFile || !sequenceConfigFile.content) {
      sequenceConfigFile = {
        content: { sequence: { state_machine: { states: { transition_states: {} } } } }
      }
    }

    const sequenceFiles = selectedFiles.filter(file => file.name?.toLowerCase() !== 'sequence_config.json');
    if (!sequenceFiles.length > 0) return null;  // Safety check
    const mergedData = mergeSequenceData(sequenceFiles, sequenceConfigFile.content, flowSequences, uploadedFiles);
    return mergedData;
  }


  // Function to merge sequence files with sequence config, setpoints, and uploaded files
  const mergeSequenceData = (fileList, sequenceConfig, setpoints, uploadedFiles) => {
    // Extract states from sequence config
    const states = sequenceConfig.sequence.state_machine.states.transition_states;

    // Create a map of filename to state object for quick lookup
    const stateByFilenameMap = new Map();
    Object.keys(states).forEach(stateKey => {
      const state = states[stateKey];
      stateByFilenameMap.set(state.sequencefilename, {
        ...state,
        state_key: stateKey
      });
    });

    // Create a map of SequenceCode to setpoint object for quick lookup
    const setpointBySequenceCodeMap = new Map();
    // Also create a map by Code (lowercase) for fuzzy matching
    const setpointByCodeMap = new Map();
    setpoints.forEach(setpoint => {
      setpointBySequenceCodeMap.set(setpoint.SequenceCode, setpoint);
      setpointByCodeMap.set(setpoint.Code.toLowerCase(), setpoint);
    });

    // Create a map of SequenceCode to uploadedFiles object for quick lookup
    const uploadedFilesBySequenceCodeMap = new Map();
    uploadedFiles.forEach(uploadedFile => {
      uploadedFilesBySequenceCodeMap.set(uploadedFile.SequenceCode, uploadedFile);
    });

    /**
     * Helper function to extract base name from filename for fuzzy matching
     * Example: "idle_state.json" -> "idle"
     *          "initialisation_state.json" -> "initialisation"
     */
    function extractBaseName(filename) {
      // Remove .json extension
      let baseName = filename.replace('.json', '');
      // Remove common suffixes
      baseName = baseName.replace(/_state$/, '');
      return baseName.toLowerCase();
    }

    // Iterate through files first and merge related data
    const mergedData = fileList.map(file => {
      // Start with file data (always included)
      const merged = { ...file };

      // Find matching state by filename
      const stateData = stateByFilenameMap.get(file.name);

      if (stateData) {
        // Add state properties
        merged.state_key = stateData.state_key;
        merged.state_id = stateData.state_id;
        merged.state_fullname = stateData.state_fullname;
        merged.wait_for = stateData.wait_for;
        merged.is_uninterruptible = stateData.is_uninterruptible;
        merged.state_type = stateData.state_type;
        merged.predefined = stateData.predefined;

        if (stateData.parent_state) {
          merged.parent_state = stateData.parent_state;
        }

        // Find matching setpoint by state_id (SequenceCode)
        const setpointData = setpointBySequenceCodeMap.get(stateData.state_id);

        if (setpointData) {
          // Add setpoint properties
          merged.SetpointId = setpointData.SetpointId;
          merged.SetpointCode = setpointData.Code;
          merged.ModelCategoryId = setpointData.ModelCategoryId;
          merged.Type = setpointData.Type;
          merged.IsPredefined = setpointData.IsPredefined;
          merged.IsUninterruptibleSetpoint = setpointData.IsUninterruptible;

          // Find matching uploadedFile by state_id (SequenceCode)
          const uploadedFileData = uploadedFilesBySequenceCodeMap.get(merged.SetpointCode);

          if (uploadedFileData) {
            // Add uploaded file properties
            merged.SequenceVersionFileId = uploadedFileData.SequenceVersionFileId;
            merged.SequenceVersionId = uploadedFileData.SequenceVersionId;
            merged.SequenceStateId = uploadedFileData.SequenceStateId;
            merged.ShowCommand = uploadedFileData.ShowCommand;
            merged.SequenceName = uploadedFileData.SequenceName;
            merged.SequenceGroupName = uploadedFileData.SequenceGroupName;
            merged.SequenceCodeNo = uploadedFileData.SequenceCodeNo;
            merged.HasSuperState = uploadedFileData.HasSuperState;
            merged.WaitFor = uploadedFileData.WaitFor;
            merged.SuperStateId = uploadedFileData.SuperStateId;

          }
        }

      } else {
        // No state found, but try to match setpoint directly by filename
        const baseName = extractBaseName(file.name);
        const setpointData = setpointByCodeMap.get(baseName);

        if (setpointData) {
          // Add setpoint properties even without state data
          merged.SetpointId = setpointData.SetpointId;
          merged.SetpointCode = setpointData.Code;
          merged.SequenceCode = setpointData.SequenceCode;
          merged.ModelCategoryId = setpointData.ModelCategoryId;
          merged.Type = setpointData.Type;
          merged.IsPredefined = setpointData.IsPredefined;
          merged.IsUninterruptibleSetpoint = setpointData.IsUninterruptible;
          merged.MatchedVia = 'setpoint_direct'; // Add flag to indicate direct setpoint match

          // Try to find matching uploadedFile by SequenceCode from setpoint
          const uploadedFileData = uploadedFilesBySequenceCodeMap.get(merged.SetpointCode);

          if (uploadedFileData) {
            // Add uploaded file properties
            merged.SequenceVersionFileId = uploadedFileData.SequenceVersionFileId;
            merged.SequenceVersionId = uploadedFileData.SequenceVersionId;
            merged.SequenceStateId = uploadedFileData.SequenceStateId;
            merged.ShowCommand = uploadedFileData.ShowCommand;
            merged.SequenceName = uploadedFileData.SequenceName;
            merged.SequenceGroupName = uploadedFileData.SequenceGroupName;
            merged.SequenceCodeNo = uploadedFileData.SequenceCodeNo;
            merged.HasSuperState = uploadedFileData.HasSuperState;
            merged.WaitFor = uploadedFileData.WaitFor;
            merged.SuperStateId = uploadedFileData.SuperStateId;
          }
        }
      }

      return merged;
    });

    // Sort by state_id or sequence_code if available, otherwise keep original order
    mergedData.sort((a, b) => {
      const aId = a.state_id !== undefined ? a.state_id : (a.sequence_code !== undefined ? a.sequence_code : 99999);
      const bId = b.state_id !== undefined ? b.state_id : (b.sequence_code !== undefined ? b.sequence_code : 99999);
      return aId - bId;
    });

    return mergedData;
  }


  //Verifying that all sequences referenced in sequence_config.json are present in flowSequences
  const verifiySequenceInFlowSequences = (selectedFiles) => {

    let mergedData = null;
    if (selectedFiles && selectedFiles.length > 0) {
      mergedData = mergeSetpointsSequenceConfigAndUploadedFileToSelectedFiles(selectedFiles);
    } else {
      mergedData = []
    }

    //verifying flow sequence setpoints
    const filesWithoutSetpoint = mergedData && mergedData.filter(md => md.SetpointCode === undefined || md.SetpointCode === null) || [];
    if (filesWithoutSetpoint.length > 0) {
      setFilesToBeUploaded([]);
      setError(`${filesWithoutSetpointError}`);
      setFilesWithErrors(new Set(filesWithoutSetpoint.map(fws => fws.id)));
      return null
    }


    // Clear previous related errors if all sequences are present
    setError('');
    setFilesWithErrors(new Set());
    return mergedData;

  }

  const dropzoneClass = `${s.dropzoneArea} ${isDragging ? s.isDragging : ''}`;

  return (
    <div className={s.sequenceFileDropzoneContainer}>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={dropzoneClass}
        onClick={() => document.getElementById('fileInput').click()}
      >
        <input
          id="fileInput"
          type="file"
          accept=".json,application/json"
          multiple
          onChange={handleFileInput}
          className={s.fileInputHidden}
        />
        <p className={s.dropzoneMessage}>
          {isDragging ? dropFileMessage : dragDropFileMessage}
        </p>
        <p className={s.jsonSupportMessage}>
          {jsonFileSupportMessage}
        </p>
      </div>

      {error && (
        <div className={s.errorMessage}>
          {error}
        </div>
      )}

      {files.length > 0 && (
        <div className={s.selectedFilesContainer}>
          <div className={s.headerActions}>
            <h4><FormattedMessage id="general.selectedFiles" defaultMessage="Selected Files " />
              <span className={s.circle}>{files.length}</span>
            </h4>
            <Button
              variant="outline-secondary"
              onClick={clearAll}            >
              <FormattedMessage id="common.clearAll" defaultMessage="Clear All" />
            </Button>
          </div>

          {files.map((file) => {
            let clsfileRow = filesWithErrors.has(file.id) || (fileUploadStatus && fileUploadStatus[file.id]?.status == false) ? s.fileItemError : '';
            if(!clsfileRow && fileUploadStatus && fileUploadStatus[file.id]?.status == true){
              clsfileRow = s.fileItemSuccess
            }

            return (
              <div
                key={file.id}
                className={`${s.fileItem} ${clsfileRow}`}
              >
                <Row className={s.fileInfoCol}>
                  <Col xs={12} sm={8} lg={9} className={s.fileNameSize}>
                    <strong>{file.name}</strong>
                    <span className={s.fileSize}>
                      ({(file.size / 1024).toFixed(2)} KB) {fileUploadStatus && fileUploadStatus[file.id]?.message || ''}
                    </span>
                  </Col>
                  <Col xs={12} sm={4} lg={3} className="d-flex justify-content-end align-items-center">
                    {viewFileId === file.id ? (
                      <Button
                        variant="outline-secondary"
                        className="ms-2"
                        onClick={() => { viewFile(0) }}
                        size="sm"
                      >
                        <FormattedMessage id="common.close" defaultMessage="Close" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline-secondary"
                        className="ms-2"
                        onClick={() => { viewFile(file.id) }}
                        size="sm"
                      >
                        <FormattedMessage id="common.view" defaultMessage="View" />
                      </Button>
                    )}
                    <Button
                      variant="outline-secondary"
                      onClick={() => removeFile(file.id)}
                      size="sm"
                      className="ms-2"
                    >
                      <FormattedMessage id="common.remove" defaultMessage="Remove" />
                    </Button>
                  </Col>
                </Row>

                {viewFileId === file.id &&
                  <Row>
                    <Col>
                      <pre className={s.fileContentPreview}>
                        {JSON.stringify(file.content, null, 2)}
                      </pre>
                    </Col>
                  </Row>
                }
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
};

SequenceFileDropzone.propTypes = {
  uploadedFiles: PropTypes.array,
  flowSequences: PropTypes.array,
  intl: PropTypes.object.isRequired,
  onUploadComplete: PropTypes.func,
  onFileUploadStatusChangeCallback: PropTypes.func
};

export default injectIntl(SequenceFileDropzone);